package fr.frux.feature.site.detail

import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.semantics.heading
import androidx.compose.ui.semantics.semantics
import fr.frux.core.designsystem.components.FruxButton
import fr.frux.core.designsystem.components.FruxMinTouchTarget
import fr.frux.core.designsystem.theme.FruxSpacing
import fr.frux.feature.site.resources.Res
import fr.frux.feature.site.resources.site_harvests_at_home
import fr.frux.feature.site.resources.site_harvests_empty
import fr.frux.feature.site.resources.site_harvests_participants
import fr.frux.feature.site.resources.site_harvests_participants_of
import fr.frux.feature.site.resources.site_harvests_start
import fr.frux.feature.site.resources.site_harvests_title
import fr.frux.shared.domain.model.Harvest
import org.jetbrains.compose.resources.stringResource

/**
 * Récoltes à venir du lieu. [highlightFirst] : lieu privé ouvert par une récolte (maquette
 * FichePriveRecolte), la première ligne porte l'invitation. [onStartHarvest] nul : pas de droit d'en lancer.
 */
@Composable
internal fun SiteHarvestsSection(
    upcoming: List<Harvest>,
    onHarvestClick: (Long) -> Unit,
    onStartHarvest: (() -> Unit)?,
    modifier: Modifier = Modifier,
    isLoading: Boolean = false,
    highlightFirst: Boolean = false,
) {
    Column(modifier = modifier, verticalArrangement = Arrangement.spacedBy(FruxSpacing.xs)) {
        Text(
            text = stringResource(Res.string.site_harvests_title),
            style = MaterialTheme.typography.titleMedium,
            modifier = Modifier.semantics { heading() },
        )
        // Pas de « aucune récolte » tant que la première réponse n'est pas là.
        if (upcoming.isEmpty() && !isLoading) {
            Text(
                text = stringResource(Res.string.site_harvests_empty),
                style = MaterialTheme.typography.bodyMedium,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
            )
        }
        upcoming.forEachIndexed { index, harvest ->
            HarvestRow(harvest, highlighted = highlightFirst && index == 0, onClick = { onHarvestClick(harvest.id) })
        }
        onStartHarvest?.let {
            FruxButton(text = stringResource(Res.string.site_harvests_start), onClick = it)
        }
    }
}

@Composable
private fun HarvestRow(
    harvest: Harvest,
    highlighted: Boolean,
    onClick: () -> Unit,
) {
    Surface(
        shape = MaterialTheme.shapes.medium,
        color = if (highlighted) MaterialTheme.colorScheme.primaryContainer else MaterialTheme.colorScheme.surface,
        modifier = Modifier.fillMaxWidth(),
    ) {
        Row(
            modifier =
                Modifier
                    .fillMaxWidth()
                    .heightIn(min = FruxMinTouchTarget)
                    .clickable(onClick = onClick)
                    .padding(horizontal = FruxSpacing.sm, vertical = FruxSpacing.xs),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(FruxSpacing.sm),
        ) {
            Column(modifier = Modifier.weight(1f)) {
                Text(text = formatSiteHarvestDate(harvest.scheduledDate), style = MaterialTheme.typography.bodyLarge)
                val max = harvest.maxParticipants
                Text(
                    text =
                        if (max == null) {
                            stringResource(Res.string.site_harvests_participants, harvest.participantsCount)
                        } else {
                            stringResource(Res.string.site_harvests_participants_of, harvest.participantsCount, max)
                        },
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                )
            }
            if (harvest.hostedByOwner) {
                Surface(shape = RoundedCornerShape(50), color = MaterialTheme.colorScheme.secondaryContainer) {
                    Text(
                        text = stringResource(Res.string.site_harvests_at_home),
                        style = MaterialTheme.typography.labelMedium,
                        color = MaterialTheme.colorScheme.onSecondaryContainer,
                        modifier = Modifier.padding(horizontal = FruxSpacing.sm, vertical = FruxSpacing.xs),
                    )
                }
            }
        }
    }
}
